import { FormControl, styled, Theme } from '@nexelem/deprecated-theme';
import { IconComponentProps } from '@nexelem/icons';
import { RadioList } from '@nexelem/lists';
import { FC, Fragment, ReactNode, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { InputLabel } from '../InputLabel';
import { getLabelStyles, StyledInternalGrid } from '../Internal';

const StyledFormControl = styled(FormControl)(({ theme, error }) => {
  const typedTheme = theme as Theme;
  const { getColor } = typedTheme.palette;
  const { list } = typedTheme.client.inputs;
  const labelTextColor = error ? list.error.labelColor : list.defaultState.labelColor;
  const labelStyles = getLabelStyles(typedTheme, getColor(labelTextColor));
  return {
    width: '100%',
    '.MuiFormGroup-root[role="radiogroup"]': {
      paddingLeft: theme.spacing(0),
    },
    '.MuiFormHelperText-root': {
      ...labelStyles,
      marginLeft: theme.spacing(0),
      paddingLeft: theme.spacing(0),
    },
    '.helper-text': {
      color: getColor(list.defaultState.optionCircleColor),
    },
    '.helper-text-error': {
      color: getColor(list.error.labelColor),
    },
    '.MuiButtonBase-root': {
      padding: theme.spacing(2),
      // circle
      svg: {
        color: getColor(list.defaultState.optionCircleColor),
        fontSize: typedTheme.spacing(48),
      },
    },
    // label
    '.MuiBox-root.label': {
      padding: theme.spacing(2, 4),
    },
  };
});

export type ListFieldProps<T = string> = {
  readonly className?: string;
  readonly error?: boolean;
  readonly id?: string;
  readonly label: string;
  readonly helperText?: ReactNode;
  readonly errorMessage?: ReactNode;
  readonly HelperTextIcon?: FC<IconComponentProps>;
  readonly required?: boolean;
  readonly options: T[];
  readonly defaultValue?: T;
  readonly value?: T;
  readonly onChange: (value?: T) => void;
  readonly getLabelOption?: (value: T) => string;
  readonly renderLabel?: (value: T) => ReactNode;
  readonly maxHeight?: `${number}vh` | `${number}%` | `${number}px` | number;
};

export function ListField<T>({
  id,
  label,
  value,
  options,
  error,
  required,
  errorMessage,
  helperText,
  defaultValue,
  onChange,
  className,
  renderLabel,
  maxHeight,
  getLabelOption = (value: T) => (value as { label: string })?.label ?? value,
  HelperTextIcon = () => null,
}: ListFieldProps<T>) {
  const internalId = useMemo(() => id || uuidv4(), [id]);
  const internalHelperTextId = useMemo(() => `${internalId}-helper-text`, [internalId]);

  const handleChange = (value?: T) => {
    onChange && onChange(value);
  };
  const { Component, props: componentProps } = useMemo(() => {
    if (maxHeight) {
      return {
        Component: 'div' as const,
        props: {
          style: { maxHeight, overflow: 'auto' },
        },
      };
    }
    return {
      Component: Fragment,
      props: {},
    };
  }, [maxHeight]);

  return (
    <StyledInternalGrid className={className} error={error}>
      <StyledFormControl error={error} required={required}>
        <InputLabel className="label" label={label} required={required} />
        <Component {...componentProps}>
          <RadioList<T>
            defaultValue={defaultValue}
            getOptionLabel={getLabelOption}
            options={options}
            renderLabel={renderLabel}
            value={value}
            onChange={handleChange}
          />
        </Component>
        {!!(errorMessage || helperText) && (
          <div className="flex flex-wrap pt-2">
            <HelperTextIcon className="helper-text-icon helper-list-icon" />
            <p className={error ? 'helper-text helper-text-error' : 'helper-text'} id={internalHelperTextId}>
              {errorMessage || helperText}
            </p>
          </div>
        )}
      </StyledFormControl>
    </StyledInternalGrid>
  );
}
